import { c as _c } from "react/compiler-runtime";
import { Loading } from '@/components/Atoms/Loading/Loading';
import useTranslation from '@/hooks/useTranslation';
import classNames from '@/utils/classNames';
import ChevronDoubleDownIcon from '@bloomberg/web-icons-react/ChevronDoubleDown';
import XIcon from '@bloomberg/web-icons-react/X';
import type { ComponentProps, DialogHTMLAttributes, HTMLAttributes, ReactNode, Ref, RefObject } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FCC } from 'react-fc';
import styles from './Dialog.module.css';
import type { IDragHandlers } from './useDragToClose';
import { useDragToClose } from './useDragToClose';
interface IProps<T = any> extends IDialogControl<T>, Omit<HTMLAttributes<HTMLDialogElement>, 'title' | 'open'> {
  title: ReactNode;
  onClose?: () => void;
  maxWidth?: string;
  allowCancel?: boolean;
  className?: string;
}
interface ILazyRender<T = any> {
  fetch: () => Promise<T>;
  render: (data: T | undefined, error?: any) => ReactNode;
  placeholder?: ReactNode;
}
interface IDialogConfig<T = any> {
  /**
   * if true, the dialog will not render its children until it is opened.
   * if a function, it will be called to fetch the children when the dialog is opened.
   */
  lazy?: IDialogControl<T>['lazy'];
  /**
   * if true, will remount the dialog every time it is opened to reset the internal states
   */
  remountOnOpen?: boolean;
}
export interface IDialogControl<T = any> {
  dialogRef: RefObject<HTMLDialogElement>;
  renderChildren?: boolean;
  open: () => void;
  close: () => void;
  renderKey?: number; // used to force re-render
  lazy?: boolean | ILazyRender<T>;
}
export const useDialog = t0 => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    lazy,
    remountOnOpen
  } = t1;
  const [renderChildren, setRenderChildren] = useState(!lazy);
  const [renderKey, setRenderKey] = useState(0);
  const dialogRef = useRef(null);
  let t2;
  if ($[2] !== remountOnOpen) {
    t2 = () => {
      if (remountOnOpen) {
        setRenderKey(_temp);
      }
      setRenderChildren(true);
      dialogRef?.current?.showModal();
      dialogRef?.current?.focus();
    };
    $[2] = remountOnOpen;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const open = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => dialogRef?.current?.close();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const close = t3;
  let t4;
  if ($[5] !== lazy || $[6] !== open || $[7] !== renderChildren || $[8] !== renderKey) {
    t4 = {
      renderKey,
      lazy,
      dialogRef,
      open,
      close,
      renderChildren
    };
    $[5] = lazy;
    $[6] = open;
    $[7] = renderChildren;
    $[8] = renderKey;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export function Dialog<T = any>({
  title,
  maxWidth,
  children,
  className,
  dialogRef,
  close,
  renderKey,
  lazy,
  onClose,
  allowCancel = true,
  renderChildren = true,
  open: _,
  // open prop is only used by consumers of useDialog, so we ignore it here
  ...rest
}: IProps<T>) {
  const [lazyData, setLazyData] = useState<T>();
  const [error, setError] = useState();
  const [mounted, setMounted] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const isLazyLoaded = typeof lazy === 'object' && lazy !== null && 'fetch' in lazy;
  const dragHandlers = useDragToClose(dialogRef);
  const handleData = async () => {
    if (renderChildren && isLazyLoaded) {
      try {
        setLazyData(undefined);
        const data = await lazy.fetch();
        setLazyData(data);
      } catch (e) {
        setLazyData(null);
        setError(e);
      }
    }
  };
  useEffect(() => {
    setMounted(true);
    handleData();
  }, [renderKey, lazy, renderChildren]);
  useEffect(() => {
    if (dialogRef.current && onClose) {
      dialogRef.current.addEventListener('close', onClose);
      return () => {
        dialogRef.current?.removeEventListener('close', onClose);
      };
    }
  }, [dialogRef, onClose]);
  useEffect(() => {
    const headerEl = headerRef.current;
    const dialogEl = dialogRef.current;
    if (!headerEl || !dialogEl || !renderChildren) {
      return;
    }
    const updateHeaderHeight = () => {
      dialogEl.style.setProperty('--_dialog-header-height', `${headerEl.offsetHeight}px`);
    };
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [dialogRef, renderChildren, renderKey, mounted]);

  // dialogs cannot render on the server, so exit early
  if (!mounted) {
    return null;
  }

  // React/Typescript doesn't recognise closedby as it's so new, so we cast it to DialogHTMLAttributes
  // This disables the ability to close the dialog using the escape key
  const closedByProp = {
    closedby: !allowCancel ? 'none' : undefined
  } as DialogHTMLAttributes<HTMLDialogElement>;
  const placeholder = isLazyLoaded ? (lazy as ILazyRender)?.placeholder : <Loading />;
  let renderedChildren: ReactNode = <DialogPlaceholder>{placeholder}</DialogPlaceholder>;
  if (renderChildren) {
    if (isLazyLoaded && lazyData !== undefined) {
      renderedChildren = (lazy as ILazyRender).render(lazyData, error);
    } else if (children) {
      renderedChildren = children;
    }
  }
  return createPortal(<dialog {...closedByProp} ref={dialogRef} className={classNames(styles.dialog, className)} style={{
    ['--_max-dialog-width' as string]: maxWidth
  }} tabIndex={-1} aria-labelledby={renderChildren ? titleId : undefined} data-cds-sizing="regular" {...rest}>
            {renderChildren && <>
                    <DialogHeader ref={headerRef} title={title} titleId={titleId} allowCancel={allowCancel} onCancel={close} dragHandlers={allowCancel ? dragHandlers : undefined} />
                    <DialogContent key={renderKey}>{renderedChildren}</DialogContent>
                </>}
        </dialog>, document.body // This renders the dialog at the end of the body so it isn't affected by inheritance
  );
}
export const DialogHeader: FCC<{
  title: ReactNode;
  titleId?: string;
  allowCancel: boolean;
  onCancel?: () => void;
  ref?: Ref<HTMLDivElement>;
  dragHandlers?: IDragHandlers;
}> = t0 => {
  const $ = _c(21);
  const {
    title,
    titleId,
    allowCancel,
    onCancel,
    ref,
    dragHandlers
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = !!dragHandlers;
  let t2;
  if ($[0] !== t1) {
    t2 = classNames(styles.headerBar, {
      [styles.headerBarDraggable]: t1
    });
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== dragHandlers) {
    t3 = dragHandlers && <div className={styles.dragHandle} aria-hidden="true" />;
    $[2] = dragHandlers;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== title || $[5] !== titleId) {
    t4 = <h2 id={titleId} className="cds-t-h4">{title}</h2>;
    $[4] = title;
    $[5] = titleId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== allowCancel || $[8] !== dragHandlers || $[9] !== onCancel || $[10] !== t) {
    t5 = allowCancel && <button aria-label={t("Actions.close_modal")} className="cds-button--tertiary cds-button--icon" onClick={onCancel}>{dragHandlers ? <><XIcon className={styles.closeIconDesktop} /><ChevronDoubleDownIcon className={styles.closeIconMobile} /></> : <XIcon />}</button>;
    $[7] = allowCancel;
    $[8] = dragHandlers;
    $[9] = onCancel;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className={styles.header}>{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== dragHandlers || $[16] !== ref || $[17] !== t2 || $[18] !== t3 || $[19] !== t6) {
    t7 = <div ref={ref} className={t2} {...dragHandlers}>{t3}{t6}</div>;
    $[15] = dragHandlers;
    $[16] = ref;
    $[17] = t2;
    $[18] = t3;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
export const DialogContent: FCC = t0 => {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.content, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const DialogPlaceholder: FCC = t0 => {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.placeholder, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const DialogActions: FCC<{
  responsive?: boolean;
  standalone?: boolean;
} & ComponentProps<'div'>> = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let rest;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      className,
      standalone: t1,
      responsive: t2,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const standalone = t1 === undefined ? false : t1;
  const responsive = t2 === undefined ? true : t2;
  const t3 = !standalone;
  const t4 = !responsive;
  let t5;
  if ($[6] !== className || $[7] !== t3 || $[8] !== t4) {
    t5 = classNames(styles.actions, {
      [styles.actionsSticky]: t3,
      [styles.actionsInline]: t4
    }, className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children || $[11] !== rest || $[12] !== t5) {
    t6 = <div className={t5} {...rest}>{children}</div>;
    $[10] = children;
    $[11] = rest;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
export const DialogActionsMain: FCC = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.actionsMain, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const DialogActionsSecondary: FCC = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.actionsSecondary, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
function _temp(prevKey) {
  return prevKey + 1;
}